import { useMemo, useState } from 'react';
import { Card, ButtonGroup, Button, Spinner } from 'react-bootstrap';
import ReactEChartsCore from '../charts/EChart';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { useAppContext } from '../../providers/AppProvider.js';

echarts.use([LineChart]);

const COLORS = [
  '#3874ff', '#0fc6c2', '#e5780b', '#8b5cf6', '#f43f5e',
  '#10b981', '#f59e0b', '#6366f1', '#ec4899', '#14b8a6'
];

const fmtDur = sec => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

/**
 * Speed and downtime per operator over time.
 *
 * The matrices answer "who is faster at this"; this answers "compared with last
 * week". Without it a ninety-day median cannot distinguish an operator who is
 * learning from a line that is quietly degrading.
 *
 * Gaps are left as gaps: a week an operator did not run this work is null, not
 * zero, so the line breaks instead of diving to the axis and implying they
 * produced at 0 m/min.
 */
const OperatorTrendChart = ({ rows = [], buckets = [], isLoading, bucket, onBucketChange }) => {
  const { getThemeColor } = useAppContext();
  const [metric, setMetric] = useState('speed'); // 'speed' | 'downtime'

  const { series, operators } = useMemo(() => {
    const byOp = new Map();
    for (const r of rows) {
      if (!byOp.has(r.operator)) byOp.set(r.operator, new Map());
      byOp.get(r.operator).set(r.bucket, r);
    }
    const ops = [...byOp.keys()].sort();
    return {
      operators: ops,
      series: ops.map((op, i) => ({
        name: op,
        type: 'line',
        smooth: false,
        connectNulls: false,
        symbolSize: 7,
        lineStyle: { width: 2 },
        itemStyle: { color: COLORS[i % COLORS.length] },
        data: buckets.map(b => {
          const cell = byOp.get(op)?.get(b);
          if (!cell) return null;
          return metric === 'speed'
            ? cell.speed
            : cell.downtimeSec > 0
              ? Number((cell.downtimeSec / 60).toFixed(1))
              : null;
        })
      }))
    };
  }, [rows, buckets, metric]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        backgroundColor: getThemeColor('gray-100'),
        borderColor: getThemeColor('gray-300'),
        textStyle: { color: getThemeColor('dark') },
        borderWidth: 1,
        valueFormatter: v =>
          v == null ? '—' : metric === 'speed' ? `${v} m/min` : `${v} min`
      },
      legend: { top: 0, textStyle: { color: getThemeColor('gray-700'), fontSize: 11 } },
      grid: { left: 10, right: 20, top: 40, bottom: 10, containLabel: true },
      xAxis: {
        type: 'category',
        data: buckets,
        boundaryGap: false,
        axisLabel: { color: getThemeColor('gray-800'), fontSize: 10, rotate: buckets.length > 10 ? 30 : 0 },
        axisTick: false,
        axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
      },
      yAxis: {
        type: 'value',
        name: metric === 'speed' ? 'm/min' : 'min lost (lower is better)',
        nameTextStyle: { color: getThemeColor('gray-600') },
        axisLabel: { color: getThemeColor('gray-600'), fontSize: 11 },
        splitLine: { lineStyle: { color: getThemeColor('gray-200') } },
        axisLine: { show: false }
      },
      series
    }),
    [series, buckets, metric, getThemeColor]
  );

  const totalDowntime = rows.reduce((s, r) => s + (r.downtimeSec || 0), 0);

  return (
    <Card>
      <Card.Header className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h6 className="mb-0">Trend over time</h6>
          <p className="mb-0 fs-10 text-700">
            {operators.length} {operators.length === 1 ? 'operator' : 'operators'} ·{' '}
            {buckets.length} {bucket === 'day' ? 'days' : 'weeks'}
            {metric === 'downtime' && totalDowntime > 0
              ? ` · ${fmtDur(totalDowntime)} lost in total`
              : ''}
          </p>
        </div>

        <div className="d-flex gap-2">
          <ButtonGroup size="sm">
            <Button
              variant={metric === 'speed' ? 'primary' : 'outline-primary'}
              onClick={() => setMetric('speed')}
            >
              Speed
            </Button>
            <Button
              variant={metric === 'downtime' ? 'warning' : 'outline-warning'}
              onClick={() => setMetric('downtime')}
            >
              Downtime
            </Button>
          </ButtonGroup>

          <ButtonGroup size="sm">
            <Button
              variant={bucket === 'week' ? 'secondary' : 'outline-secondary'}
              onClick={() => onBucketChange('week')}
            >
              Weekly
            </Button>
            <Button
              variant={bucket === 'day' ? 'secondary' : 'outline-secondary'}
              onClick={() => onBucketChange('day')}
            >
              Daily
            </Button>
          </ButtonGroup>
        </div>
      </Card.Header>

      <Card.Body className="p-0" style={{ minHeight: 320 }}>
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center py-6 gap-2">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="text-700 fs-9">Loading…</span>
          </div>
        ) : !buckets.length ? (
          <div className="text-center py-6 text-700 fs-9">
            Not enough history yet — the trend fills in as production accumulates.
          </div>
        ) : (
          <ReactEChartsCore
            echarts={echarts}
            option={option}
            style={{ height: '340px', width: '100%' }}
            notMerge
            lazyUpdate
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default OperatorTrendChart;
